import { Injectable } from '@angular/core';

import { ENV } from '@app/env';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { APP_CONFIG } from '../../app.config';
import { UtilHelper } from '../../shared/helpers/utils.helper';
import { ConfirmationResponse } from '../../shared/models/communication-response.model';
import { RequestService } from '../../shared/services/request.service';
import { PeriodType } from './enums/period-type.enum';
import { ReservationConfigType } from './enums/reservation-config-type.enum';
import { ReservationStatus } from './enums/reservation-status.enum';
import { ReservationType } from './enums/reservation-type.enum';
import {
  GroupHourAvailability,
  HourAvailability,
  ReservationAvailability,
} from './models/reservation-availability.model';
import { ReservationConfig } from './models/reservation-config.model';
import {
  ReservationDayHours,
  ReservationDayWeek,
  ReservationHourList,
} from './models/reservation-day-hours-config.model';
import { Reservation, ShortReservationReport } from './models/reservation.model';
import { TableSector, TableSectorConfig } from './models/table-sector.model';

@Injectable()
export class ReservationService {
  constructor(
    private requestService: RequestService,
    private translateService: TranslateService
  ) {}

  async getReservationConfig(
    storeId: number,
    canBeNew: boolean,
    configType?: ReservationConfigType,
    specialDate?: string
  ): Promise<ReservationConfig> {
    let urlRequest: string = this.requestService.buildUrlPathParams(
      {
        storeId: storeId,
        configType: configType || ReservationConfigType.DEFAULT,
      },
      APP_CONFIG.WEBSERVICE.GET_RESERVATION_CONFIG
    );
    if (configType == ReservationConfigType.SPECIAL_DATE && specialDate) {
      urlRequest += `&date=${moment(specialDate).format('YYYY-MM-DD')}`;
    }
    const response = await this.requestService.getData(urlRequest);
    if (response) {
      return new ReservationConfig(response);
    } else if (canBeNew) {
      const config = new ReservationConfig();
      config.storeId = storeId;
      config.configType = configType || ReservationConfigType.DEFAULT;
      return config;
    }
    return response;
  }

  async getReservationConfigById(configId: number): Promise<ReservationConfig> {
    let urlRequest: string = `${APP_CONFIG.WEBSERVICE.RESERVATION_CONFIG_V2}/${configId}`;
    const response = await this.requestService.getData(urlRequest);
    return new ReservationConfig(response);
  }

  async saveReservationConfig(reservationConfig: ReservationConfig): Promise<any> {
    const response: any = await this.requestService.postData(
      APP_CONFIG.WEBSERVICE.RESERVATION_CONFIG_V2,
      reservationConfig.toJson(),
      true
    );
    return UtilHelper.getIdFromLocationPath(response.location);
  }

  async updateReservationConfig(reservationConfig: ReservationConfig): Promise<ReservationConfig> {
    const response = await this.requestService.putData(
      `${APP_CONFIG.WEBSERVICE.RESERVATION_CONFIG_V2}/${reservationConfig.id}`,
      reservationConfig.toJson()
    );
    if (response) {
      return new ReservationConfig(response);
    }
  }

  async getReservationTables(storeId: number): Promise<ReservationDayHours[]> {
    const urlRequest: string = this.requestService.buildUrlPathParams(
      { storeId: storeId },
      APP_CONFIG.WEBSERVICE.GET_RESERVATION_TABLES
    );
    const response: ReservationDayHours[] = (await this.requestService.getData(urlRequest)) || [];
    if (response && response.length > 0) {
      response.forEach((day, index) => {
        response[index] = new ReservationDayHours(day);
        if (response[index].hours && response[index].hours.length > 0) {
          response[index].hours.sort((h1, h2) => {
            return h1.initialTime > h2.initialTime ? 1 : -1;
          });
        }
      });
    }
    return response;
  }

  async saveReservationHours(
    daysOfWeek: ReservationDayWeek,
    periodType: PeriodType
  ): Promise<void> {
    const days: ReservationDayWeek = JSON.parse(JSON.stringify(daysOfWeek));
    const jsonData: ReservationDayWeek = {
      storeId: days.storeId,
      daysList: [],
    };
    days.daysList.forEach((d) => {
      if (d.enable) {
        if (d.hours && d.hours.length > 0) {
          d.hours.forEach((dh) => {
            dh.charge = dh.reservationFee != null && dh.reservationFee > 0;
            if (periodType == PeriodType.HOUR) {
              delete dh.endTime;
            }
          });
        }
        jsonData.daysList.push({
          id: d.id,
          hours: d.hours,
          dayOfWeek: d.dayOfWeek,
        });
      }
    });
    console.log(jsonData);
    await this.requestService.postData(APP_CONFIG.WEBSERVICE.RESERVATION_TABLES, jsonData);
  }

  async updateReservationTables(tables: any): Promise<void> {
    await this.requestService.putData(APP_CONFIG.WEBSERVICE.RESERVATION_TABLES, tables);
  }

  async deleteReservationTables(storeId: number): Promise<void> {
    const urlRequest: string = this.requestService.buildUrlPathParams(
      { storeId: storeId },
      APP_CONFIG.WEBSERVICE.RESERVATION_TABLES
    );
    await this.requestService.deleteData(urlRequest);
  }

  //SECTORS
  async postReservationSectors(storeId: number, sectors: TableSector[]): Promise<any> {
    const urlRequest: string = this.requestService.buildUrlPathParams(
      { storeId: storeId },
      APP_CONFIG.WEBSERVICE.RESERVATION_SECTORS
    );
    return await this.requestService.postData(urlRequest, sectors);
  }

  async getReservationSectors(storeId: number): Promise<TableSector[]> {
    const urlRequest: string = this.requestService.buildUrlPathParams(
      { storeId: storeId },
      APP_CONFIG.WEBSERVICE.RESERVATION_SECTORS + '?status=true'
    );
    const response = (await this.requestService.getData(urlRequest)) || [];
    const sectors: TableSector[] = [];
    response.forEach((sector) => {
      sectors.push(new TableSector(sector));
    });
    return sectors;
  }

  async getReservationSectorsHours(storeId: number): Promise<ReservationDayHours[]> {
    const urlRequest: string = this.requestService.buildUrlPathParams(
      { storeId: storeId },
      APP_CONFIG.WEBSERVICE.RESERVATION_SECTORS_HOURS
    );
    let response: ReservationDayHours[] = (await this.requestService.getData(urlRequest)) || [];
    if (response && response.length > 0) {
      response.forEach((day) => {
        if (day.hours && day.hours.length > 0) {
          day.hours.sort((h1, h2) => {
            return h1.initialTime > h2.initialTime ? 1 : -1;
          });
        }
      });
    }
    return response || [];
  }

  async saveReservationSectorsHours(
    storeId: number,
    daysOfWeek: ReservationDayWeek,
    periodType: PeriodType
  ): Promise<void> {
    const days: ReservationDayWeek = JSON.parse(JSON.stringify(daysOfWeek));
    const jsonData = [];
    days.daysList.forEach((d) => {
      if (d.enable) {
        if (d.hours && d.hours.length > 0) {
          d.hours.forEach((dh) => {
            if (periodType == PeriodType.HOUR) {
              delete dh.endTime;
            }
          });
        }
        jsonData.push({
          id: d.id,
          hours: d.hours,
          dayOfWeek: d.dayOfWeek,
          storeId: storeId,
        });
      }
    });
    console.log(jsonData);
    const urlRequest: string = this.requestService.buildUrlPathParams(
      { storeId: storeId },
      APP_CONFIG.WEBSERVICE.RESERVATION_SECTORS_HOURS
    );
    await this.requestService.postData(urlRequest, jsonData);
  }

  //TABLESCONF
  async saveTableSeats(storeId: number, tableconf: TableSectorConfig[]): Promise<void> {
    const urlRequest: string = this.requestService.buildUrlPathParams(
      { storeId: storeId },
      APP_CONFIG.WEBSERVICE.TABLES_CONF
    );
    await this.requestService.postData(urlRequest, tableconf);
  }

  async getTableConf(storeId: number): Promise<TableSectorConfig[]> {
    const urlRequest: string = this.requestService.buildUrlPathParams(
      { storeId: storeId },
      APP_CONFIG.WEBSERVICE.TABLES_CONF + '?status=true'
    );
    let response: TableSectorConfig[] = (await this.requestService.getData(urlRequest)) || [];
    console.log(response);
    if (response && response.length > 0) {
      response.sort((s1, s2) => {
        return s1.sectorName > s2.sectorName ? 1 : -1;
      });
    }
    return response || [];
  }

  async getReservationAvailability(
    storeId: number,
    startDate: string,
    endDate: string
  ): Promise<Array<ReservationAvailability>> {
    const urlRequest: string = this.requestService.buildUrlPathParams(
      { storeId: storeId, startDate: startDate, endDate: endDate },
      APP_CONFIG.WEBSERVICE.GET_RESERVATION_AVAILABILITY
    );
    return (await this.requestService.getData(urlRequest)) || [];
  }

  async updateReservationAvailability(
    storeId: number,
    date: string,
    status: ReservationStatus
  ): Promise<void> {
    const data: any = {
      store: { id: storeId },
      status: status,
      startDate: date,
      endDate: date,
    };
    (await this.requestService.putData(APP_CONFIG.WEBSERVICE.RESERVATION_AVAILABILITY, data)) || [];
  }

  async getReservationList(
    storeId: number,
    status: string,
    typeOfQuery: string,
    reservationDate: string,
    size?: number,
    page?: number
  ): Promise<Array<Reservation>> {
    let urlRequest: string = '';
    switch (status) {
      case ReservationStatus.APPROVED:
        urlRequest = this.requestService.buildUrlPathParams(
          {
            storeId: storeId,
            reservationDate: reservationDate,
            typeOfQuery: typeOfQuery,
          },
          APP_CONFIG.WEBSERVICE.GET_APPROVED_RESERVATION_LIST
        );
        break;

      case ReservationStatus.WAITING_APPROVAL:
      case ReservationStatus.WAITING_PAYMENT:
      case `${ReservationStatus.WAITING_APPROVAL},${ReservationStatus.WAITING_PAYMENT}`:
        urlRequest = this.requestService.buildUrlPathParams(
          { storeId: storeId, status: status },
          APP_CONFIG.WEBSERVICE.GET_WAITING_RESERVATION_LIST
        );
        break;

      default: // CANCELED
        urlRequest = this.requestService.buildUrlPathParams(
          { storeId: storeId, status: status, size: size, page: page },
          APP_CONFIG.WEBSERVICE.GET_CANCELED_RESERVATION_LIST
        );
        break;
    }
    const response = await this.requestService.getData(urlRequest);
    const responseReservations: Array<Reservation> = [];
    if (response && response.length > 0) {
      for (const r of response) {
        responseReservations.push(new Reservation(r));
      }
    }
    return responseReservations;
  }

  async cancelReservation(id: number, reason: string): Promise<ConfirmationResponse> {
    const data: any = {
      id: id,
      noteReject: reason,
      customer: false,
    };
    return await this.requestService.putData(APP_CONFIG.WEBSERVICE.CANCEL_RESERVATION, data);
  }

  async getReservationsHoursAvailability(
    storeId: number,
    date: string
  ): Promise<HourAvailability[]> {
    const urlRequest: string = this.requestService.buildUrlPathParams(
      {
        storeId: storeId,
        date: date,
      },
      APP_CONFIG.WEBSERVICE.GET_RESERVATION_HOURS_AVAILABILITY
    );
    let response: Array<any> = await this.requestService.getData(urlRequest);
    console.log(response);
    let hours: HourAvailability[] = [];
    if (response && response.length > 0) {
      response.sort((h1, h2) => {
        return h1.hour > h2.hour ? 1 : -1;
      });
      for (const item of response) {
        hours.push(new HourAvailability(item));
      }
    }
    return hours || [];
  }

  async createReservation(
    reservation: Reservation,
    paymentAccountStatusError: any
  ): Promise<ConfirmationResponse> {
    console.log(reservation.toJsonCreate(paymentAccountStatusError));
    return await this.requestService.postData(
      APP_CONFIG.WEBSERVICE.RESERVATION,
      reservation.toJsonCreate(paymentAccountStatusError)
    );
  }

  async updateReservation(reservation: Reservation): Promise<ConfirmationResponse> {
    console.log(reservation.toJsonUpdate());
    return await this.requestService.putData(
      APP_CONFIG.WEBSERVICE.RESERVATION,
      reservation.toJsonUpdate()
    );
  }

  async approveReservation(id: number, overbooking?: boolean): Promise<ConfirmationResponse> {
    const data: any = { id: id };
    if (overbooking) {
      data.overbooking = true;
    }
    return await this.requestService.putData(APP_CONFIG.WEBSERVICE.APPROVE_RESERVATION, data);
  }

  async checkin(id: number, present: boolean): Promise<void> {
    const data: any = { id: id, present: present };
    await this.requestService.putData(APP_CONFIG.WEBSERVICE.RESERVATION_CHECKIN, data);
  }

  async updateReservationConfigStatus(status: string, configId: number): Promise<void> {
    const data = {
      id: configId,
      status: status,
    };
    await this.requestService.putData(APP_CONFIG.WEBSERVICE.RESERVATION_CONFIG_STATUS, data);
  }

  async deleteReservationConfig(configId: number): Promise<void> {
    const urlRequest: string = this.requestService.buildUrlPathParams(
      { configId: configId },
      APP_CONFIG.WEBSERVICE.REMOVE_RESERVATION_CONFIG
    );
    await this.requestService.deleteData(urlRequest);
  }

  // SPECIAL DATE
  // -----------------------------------------------

  async getSpecialDates(storeId: number): Promise<ReservationConfig[]> {
    const urlRequest: string = this.requestService.buildUrlPathParams(
      { storeId: storeId },
      APP_CONFIG.WEBSERVICE.GET_SPECIAL_DATES
    );
    const response = (await this.requestService.getData(urlRequest)) || [];
    const specialDates: ReservationConfig[] = [];
    response.forEach((config) => {
      specialDates.push(new ReservationConfig(config));
    });
    return specialDates;
  }

  async saveSpecialDateTableSeats(configId: number, tableconf: TableSectorConfig[]): Promise<void> {
    const urlRequest: string = this.requestService.buildUrlPathParams(
      { configId: configId },
      APP_CONFIG.WEBSERVICE.SPECIAL_DATE_SEATS_CONFIG
    );
    await this.requestService.postData(urlRequest, tableconf);
  }

  async getSpecialDateTableSeats(configId: number): Promise<TableSectorConfig[]> {
    const urlRequest: string = this.requestService.buildUrlPathParams(
      { configId: configId },
      APP_CONFIG.WEBSERVICE.SPECIAL_DATE_SEATS_CONFIG + '?status=true'
    );
    let response: TableSectorConfig[] = (await this.requestService.getData(urlRequest)) || [];
    console.log(response);
    if (response && response.length > 0) {
      response.sort((s1, s2) => {
        return s1.sectorName > s2.sectorName ? 1 : -1;
      });
    }
    return response || [];
  }

  async getSpecialDateHours(configId: number): Promise<ReservationDayHours[]> {
    const urlRequest: string = this.requestService.buildUrlPathParams(
      { configId: configId },
      APP_CONFIG.WEBSERVICE.SPECIAL_DATE_HOURS_AVAILABILITY
    );
    const response: ReservationDayHours[] = (await this.requestService.getData(urlRequest)) || [];
    if (response && response.length > 0) {
      response.forEach((day, index) => {
        response[index] = new ReservationDayHours(day);
        if (response[index].hours && response[index].hours.length > 0) {
          response[index].hours.sort((h1, h2) => {
            return h1.initialTime > h2.initialTime ? 1 : -1;
          });
        }
      });
    }
    return response;
  }

  async saveSpecialDateHours(
    configId: number,
    specialDate: ReservationDayHours,
    periodType: PeriodType,
    reservationType: ReservationType
  ): Promise<void> {
    const jsonData: any = [];
    specialDate.hours.forEach((dh) => {
      if (reservationType == ReservationType.PEOPLE) {
        dh.charge = dh.reservationFee != null && dh.reservationFee > 0;
      }
      if (periodType == PeriodType.HOUR) {
        delete dh.endTime;
      }
    });
    jsonData.push({
      id: specialDate.id,
      hours: specialDate.hours,
      dayOfWeek: specialDate.dayOfWeek,
    });
    const urlRequest: string = this.requestService.buildUrlPathParams(
      { configId: configId },
      APP_CONFIG.WEBSERVICE.SPECIAL_DATE_HOURS_AVAILABILITY
    );
    await this.requestService.postData(urlRequest, jsonData);
  }

  // HELPERS
  // -----------------------------------------------

  preparedMessageToSendByWhatsApp(
    reservation: Reservation,
    storeName: string,
    messageToUse?: string
  ): { message: string; phone: string } {
    let message = messageToUse;
    if (!message) {
      switch (reservation.status) {
        case ReservationStatus.APPROVED:
          message = 'TABLE_RESERVATION.WHATS_APPROVED_MESSAGE';
          break;

        case ReservationStatus.WAITING_APPROVAL:
          message = 'TABLE_RESERVATION.WHATS_WAITING_APPROVAL_MESSAGE';
          break;

        case ReservationStatus.WAITING_PAYMENT:
          message = 'TABLE_RESERVATION.WHATS_WAITING_PAYMENT_MESSAGE';
          break;

        case ReservationStatus.CANCELED_BY_STORE:
        case ReservationStatus.CANCELED_BY_CUSTOMER:
          message = 'TABLE_RESERVATION.WHATS_CANCELED_MESSAGE';
          break;

        default:
          break;
      }
    }
    if (message) {
      let idUrlReservation = reservation.user ? reservation.id : `p/${reservation.uniqueCode}`;
      let date = moment(reservation.reservationDate).format('DD/MM/YYYY');
      message = this.translateService.instant(message, {
        store: storeName,
        customer: reservation.customer.name || '',
        date: date,
        time: reservation.reservationTime,
        link: `${ENV.consumerWebApp}/#/reservation/${idUrlReservation}`,
      });
      let phone =
        (UtilHelper.unmask(reservation.customer.countryPhoneCode) || '55') +
        UtilHelper.unmask(reservation.customer.phone);
      return {
        message: message,
        phone: phone,
      };
    }
    return null;
  }

  validateReservationHoursConfig(
    day: ReservationDayHours,
    hour: ReservationHourList,
    shouldValidateQtde: boolean,
    periodType: PeriodType,
    isSpecialDay: boolean
  ): string {
    let qtdeError = shouldValidateQtde && !hour.quantity ? 'e quantidade ' : '';
    let dayOfWeek: string = isSpecialDay
      ? 'a data especial'
      : this.translateService.instant('DAYOFWEEK.' + day.dayOfWeek);
    if (!hour.initialTime || qtdeError) {
      return `Informe o horário ${qtdeError}para ${dayOfWeek}.`;
    }
    const initialTime: moment.Moment = moment(hour.initialTime, 'HH:mm', true);
    if (!initialTime.isValid()) {
      return `Horário inválido (${hour.initialTime}) para ${dayOfWeek}.`;
    }
    const hoursSameTime: Array<any> =
      day.hours.filter((h) => h.initialTime == hour.initialTime) || [];
    if (hoursSameTime.length > 1) {
      return `Você possui horários duplicados para ${dayOfWeek}.`;
    }
    if (periodType == PeriodType.PERIOD) {
      if (!hour.endTime) {
        return `Informe a hora fim para às ${hour.initialTime} para ${dayOfWeek}.`;
      }
      const endTime: moment.Moment = moment(hour.endTime, 'HH:mm', true);
      if (endTime.isSameOrBefore(initialTime)) {
        let defaultMessage: string = `Hora início (${hour.initialTime}) não pode ser maior ou igual a hora fim (${hour.endTime}).`;
        return isSpecialDay
          ? defaultMessage
          : `${defaultMessage} Verifique os horários de ${dayOfWeek}.`;
      }
      // Valida se tem um horário dentro de outro
      for (const hour2 of day.hours) {
        if (hour !== hour2) {
          if (hour.initialTime >= hour2.initialTime && hour.initialTime <= hour2.endTime) {
            return `Você possui períodos com conflitos de horários para ${dayOfWeek}. Revise para que cada período seja único no dia, com hora início e fim diferente entre os períodos.`;
          }
        }
      }
    }
    return null;
  }

  async getReservationSummary(storeId: number, date: string): Promise<any> {
    const urlRequest: string = this.requestService.buildUrlPathParams(
      { storeId: storeId, date: date },
      APP_CONFIG.WEBSERVICE.RESERVATION_SUMMARY
    );
    let response: { approved: number; waitingApproval: number } =
      await this.requestService.getData(urlRequest);
    console.log(response);
    const reservationSummary: any = {};
    if (response && response.approved) {
      reservationSummary.approved = {
        amount: response.approved,
        description: '',
      };
      if (response.approved > 1) {
        reservationSummary.approved.description = 'RESERVATION.RESERVATIONS_TODAY';
      } else {
        reservationSummary.approved.description = 'RESERVATION.ONE_RESERVATIONS_TODAY';
      }
    } else {
      reservationSummary.approved = {
        amount: 0,
        description: 'RESERVATION.NO_RESERVATIONS_TODAY',
      };
    }

    // Só mostra se existir
    if (response && response.waitingApproval) {
      reservationSummary.waitingApproval = {
        amount: response.waitingApproval,
        description: '',
      };
      if (response.waitingApproval > 1) {
        reservationSummary.waitingApproval.description =
          'RESERVATION.WAITING_APPROVAL_RESERVATIONS';
      } else {
        reservationSummary.waitingApproval.description =
          'RESERVATION.WAITING_APPROVAL_ONE_RESERVATIONS';
      }
    }
    return reservationSummary;
  }

  /**
	 * Constrói um objeto ShortReservationReport que retorna no formato
	 * Data: 23/05
	    11:30
	    - 4 reservas com 2 lugares
		- 3 reservas com 4 lugares
		- 2 reservas com mesa para 6 lugares (Mesas maiores)

		12:00
		- 1 reserva com 2 lugares
		- 5 reservas com 4 lugares
		- 3 reservas com 3 lugares
		....
	 * @param date 
	 * @param reservations 
	 */
  buildList(date: string, reservations: Array<Reservation>): ShortReservationReport {
    let data: ShortReservationReport = {
      date: date,
      countDescription: '',
      hours: [],
    };
    try {
      let countQuota: number = 0;
      for (const res of reservations) {
        let isTableType: boolean = (res.sectorName && res.sectorId && res.seats) != null;

        const index: number = data.hours.findIndex((h) => h.hour == res.reservationTime);
        // Hora ainda não está na lista
        if (index == -1) {
          data.hours.push({
            hour: res.reservationTime,
            count: 1,
            quota: isTableType ? res.seats : res.quota,
            shortReservations: [
              {
                quota: isTableType ? null : res.quota,
                seats: isTableType ? res.seats : null,
                sectorId: isTableType ? res.sectorId : null,
                count: 1,
                description: this.getDescriptionForShorReport(res, isTableType, 1),
              },
            ],
          });
        } else {
          data.hours[index].count += 1;
          data.hours[index].quota += isTableType ? res.seats : res.quota;

          // Verifica se já existe na lista um item com a quantidade de lugares
          const indexQuota: number = data.hours[index].shortReservations.findIndex((r) => {
            if (isTableType) {
              return r.seats == res.seats && r.sectorId == res.sectorId;
            } else {
              return r.quota == res.quota;
            }
          });
          if (indexQuota == -1) {
            data.hours[index].shortReservations.push({
              quota: isTableType ? null : res.quota,
              seats: isTableType ? res.seats : null,
              sectorId: isTableType ? res.sectorId : null,
              count: 1,
              description: this.getDescriptionForShorReport(res, isTableType, 1),
            });
          } else {
            let count = data.hours[index].shortReservations[indexQuota].count + 1;
            data.hours[index].shortReservations[indexQuota].count = count;
            data.hours[index].shortReservations[indexQuota].description =
              this.getDescriptionForShorReport(res, isTableType, count);
          }
        }
        countQuota += isTableType ? res.seats : res.quota;
      }
      data.countDescription =
        reservations.length +
        (reservations.length == 1 ? ' reserva' : ' reservas') +
        ' (total de ' +
        countQuota +
        ' lugares)';
      console.log(data);
    } catch (error) {
      console.error(error);
    }
    return data;
  }

  buildGroupList(
    hoursAvailability: HourAvailability[],
    reservationDate: Date
  ): GroupHourAvailability[] {
    const groupObj = hoursAvailability.reduce((item, curr) => {
      const key = `${curr.initialTime}-${curr.endTime}`;
      if (!item[key]) {
        item[key] = {
          item: curr,
          hours: [],
          overbookingDescription: '',
          available: true,
        };
        let description: string = '';
        let moreDetails: string = '';

        if (curr.sectors) {
          let totalTables: number = 0;
          curr.sectors.forEach((sector) => {
            if (sector.tables && sector.tables.length > 0) {
              moreDetails += `<b>${sector.name}</b>: <br>`;
              let addedTables: boolean = false;
              sector.tables.forEach((table) => {
                if (table.status) {
                  if (table.tableQuantity < 0) {
                    let tableDescription = table.tableQuantity < -1 ? 'mesas' : 'mesa';
                    item[key].overbookingDescription +=
                      `Setor ${sector.name}, das ${curr.initialTime} às ${curr.endTime} (<b>${table.tableQuantity}</b> ${tableDescription} de ${table.seatQuantity} lugares)<br>`;
                  }
                  const tbQtde = table.tableQuantity < 0 ? 0 : table.tableQuantity;
                  if (tbQtde > 0) {
                    const tbDescription: string = tbQtde > 1 ? 'mesas' : 'mesa';
                    moreDetails += `${tbQtde} ${tbDescription} de ${table.seatQuantity} lugares<br>`;
                    totalTables += tbQtde;
                    addedTables = true;
                  }
                }
              });
              if (!addedTables) {
                moreDetails += 'Nenhuma mesa disponível<br>';
              }
              moreDetails += '<br>';
            }
          });
          const tableQuantityDescription: string =
            totalTables > 1 ? `${totalTables} mesas` : 'Última mesa';
          description = totalTables > 0 ? tableQuantityDescription : 'Nenhum lugar disponível';
        } else {
          const quotaDescription: string = curr.quota > 1 ? 'lugares' : 'lugar';
          description =
            curr.quota > 0 ? `${curr.quota} ${quotaDescription}` : 'Nenhum lugar disponível';
          if (curr.quota < 0) {
            item[key].overbookingDescription +=
              `Período das ${curr.initialTime} às ${curr.endTime} (${curr.quota} lugares)<br>`;
          }
        }
        item[key].description = description;
        item[key].moreDetails = moreDetails;
      }
      // Pega o quota do primeiro item, pois na edição é adicionado o quota da reserva já feita de forma manual durante
      // o tratamento dos quotas no primeiro item da lista se for tipo período. Porém é preciso considerar esse quota para todos os itens do período
      if (item[key].item.quota > 0) {
        item[key].hours.push(curr.hour);
      } else {
        item[key].available = false;
        item[key].hours.push(curr.hour);
      }
      return item;
    }, {});

    const today: Date = new Date();
    today.setHours(0, 0, 0, 0);

    const groups: GroupHourAvailability[] = [];
    for (const key in groupObj) {
      if (groupObj.hasOwnProperty(key)) {
        const item = groupObj[key];
        // Valida se a reserva é para hoje, para não mostrar horários passados
        if (reservationDate.getTime() == today.getTime()) {
          const now: Date = new Date();
          item.hours = item.hours.filter((hour) => {
            const hourDate: Date = moment(hour, 'HH:mm').toDate();
            console.log(now, hourDate);
            return hourDate.getTime() > now.getTime();
          });
        }
        groups.push(item);
      }
    }
    console.log(groups);
    return groups;
  }

  private getDescriptionForShorReport(
    res: Reservation,
    isTableType: boolean,
    count: number
  ): string {
    const countReserves: number = count || 1;
    const countDescription: string = count > 1 ? 'reservas' : 'reserva';
    let description: string = '';
    if (isTableType) {
      description = `${countReserves} ${countDescription} com mesa para ${res.seats} lugares (${res.sectorName})`;
    } else {
      description = `${countReserves} ${countDescription} com ${res.quota} ${res.quota == 1 ? 'lugar' : 'lugares'}`;
    }
    return description;
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/reservation/reservation.service.ts